import { Tooltip } from "@chakra-ui/react";
import { Plural, Trans } from "@lingui/macro";
import { Link, ProgressBar } from "@src/components/ui-kit";
import { currency, inputPercent, percent } from "@src/utils/formatters";
import { kebabCase } from "lodash";
import { observer } from "mobx-react-lite";
import { type FinishProjectSummary, useSummaryContext } from "../models";

type TOurWorkItem = FinishProjectSummary["incomplete_our_work_budget_items"][0];
type TExpenseItem = FinishProjectSummary["incomplete_expense_budget_items"][0];

type BudgetItemProps =
  | { ourWorkItem: TOurWorkItem; expenseItem?: undefined }
  | { expenseItem: TExpenseItem; ourWorkItem?: undefined };

const BudgetItem = ({ ourWorkItem, expenseItem }: BudgetItemProps) => {
  const summary = useSummaryContext();

  if ((!ourWorkItem && !expenseItem) || (ourWorkItem && expenseItem)) {
    return null;
  }

  const title = (ourWorkItem || expenseItem)?.title;

  return (
    <div className="py-8 border-b last:border-b-0 border-sky flex justify-between items-center">
      <Link
        href={{
          pathname: "/projects/detail",
          query: { id: summary.id },
          hash: kebabCase(title),
        }}
      >
        {title}
      </Link>

      <div className="w-1/4">
        {expenseItem && (
          <Tooltip
            label={
              <div
                className="flex p-3 text-lg"
                style={{ maxWidth: 430, minWidth: 380 }}
              >
                <div className="w-1/2 pr-2 flex flex-col">
                  <h4 className="font-bold mb-2 text-white">
                    <Trans>Budget:</Trans>
                  </h4>
                  <span className="mb-2">
                    <Trans>Expenses:</Trans>{" "}
                    {currency.formatByWorkspace(
                      expenseItem.stats.real_expenses,
                    )}
                  </span>
                  <span className="mb-2">
                    <Trans>Budgeted profit estimate:</Trans>{" "}
                    {currency.formatByWorkspace(
                      expenseItem.stats.real_commission,
                    )}
                    (
                    {percent.format(expenseItem.stats.real_commission_relative)}
                    )
                  </span>
                </div>

                <div className="w-1/2 pl-2 flex flex-col">
                  <h4 className="font-bold mb-2 text-white">
                    <Trans>Spent:</Trans>
                  </h4>
                  <span className="mb-2">
                    <span className="mr-2 underline">
                      <Trans>Received invoices:</Trans>
                    </span>
                    {currency.formatByWorkspace(
                      expenseItem.stats.real_expenses,
                    )}
                  </span>
                </div>
              </div>
            }
          >
            <div>
              <ProgressBar
                bgColor="bg-white"
                progress={inputPercent.format(
                  expenseItem.stats.real_expenses_percent,
                )}
              />
            </div>
          </Tooltip>
        )}

        {ourWorkItem && (
          <Tooltip
            label={
              <div
                className="flex flex-col justify-center items-center p-3 text-lg"
                style={{ minWidth: 100 }}
              >
                <h4 className="mb-2 font-bold text-center">
                  <Trans>Progress:</Trans>
                </h4>
                <span className="mb-2">
                  {percent.format(
                    ourWorkItem.lastWorkInProgressItemStat?.progress_percent ||
                      0,
                  )}
                </span>
                <span className="mb-2">
                  {currency.formatByWorkspace(ourWorkItem.stats.spent_budget)}
                </span>
                <span className="mb-2">
                  <Plural
                    value={ourWorkItem.stats.spent_time}
                    one="# hour"
                    other="# hours"
                  />
                </span>
              </div>
            }
          >
            <div>
              <ProgressBar
                bgColor="bg-white"
                color="purple"
                progress={inputPercent.format(
                  ourWorkItem.lastWorkInProgressItemStat?.progress_percent || 0,
                )}
              />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default observer(BudgetItem);
